<template>
<section class="section speciesSearch">
  <div class="container">
    <div class="columns mb-1 is-multiline is-centered mt-4">
      <div class="column is-6-desktop is-12-tablet">
        <div class="searchForm rightButton">
          <input @input="searchForAutoCompleteDebounced(state.inFilterTerm)" v-on:keyup.enter="manualSearch" type="text"
            :placeholder="$t('search.input.message') + ' (' + $t('autoComplete.enterMoreCharacter', {characterCount: 3}) + ')'"
            name="search" v-model="state.inFilterTerm" class="input">
          <button @click="manualSearch">
            <svg class="searchIcon">
              <use xlink:href="@/assets/sprite.svg#icon-mainsearch"></use>
            </svg>
          </button>
          <div class="headSearchResults" :class="{'active': state.isAutoCompleteLoading}" v-show="state.isAutoCompleteLoading">
            <p> {{$t('common.load.loading') }} </p>
          </div>
          <div class="headSearchResults" :class="{'active': state.autoCompleteSppList?.length > 0}" v-show="state.autoCompleteSppList?.length > 0">
            <div class="resultList">
              <div class="singleResult" v-for="(item, index) in state.autoCompleteSppList" :key="'spp' + index">
                <a class="resultLink" @click="pickAutoCompleteItem(item)">
                  <div class="photo"><img style="width:40px; height: 40px;" :src="profileImageUrlPrefix + item.scientificName" alt="placeholder" class="rounded-1"></div>
                  <div class="name" v-if="item.commonName">{{item.commonName}} (<em>{{item.scientificName}}</em>)</div>
                  <div class="name" v-else><em>{{item.scientificName}}</em></div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="filterToggle">
      <div class="columns">
        <div class="column is-12 filterToggleButton">
          <p @click="showAdvancedFilter = !showAdvancedFilter">
            {{ $t("search.filter") }}
            <svg class="arrowDown">
              <use xlink:href="@/assets/sprite.svg#icon-chevron"></use>
            </svg>
          </p>
        </div>
      </div>

      <div id="filterExpand" v-if="showAdvancedFilter">
        <div class="columns is-multiline filters is-centered">
          <div class="column" :class="tableHeaderStyle">
            <custom-dropdown v-model="state.searchTerms.cname" :label="$t('search.columns.cname')" @filter:modelValue.self="filterTable" :showAlphabet="true" />
          </div>
          <div class="column" :class="tableHeaderStyle">
            <custom-dropdown v-model="state.searchTerms.sname" :label="$t('search.columns.sname') " @filter:modelValue.self="filterTable" :showAlphabet="true" />
          </div>
          <div class="column is-2-desktop is-2-tablet">
            <custom-dropdown v-model="state.searchTerms.summary" :label="$t('search.columns.summary')"  @filter:modelValue.self="filterTable" :options="modelOptions"/>
          </div>
          <div class="column is-2-desktop is-2-tablet" v-if="hasWetlandColumn">
            <custom-dropdown v-model="state.searchTerms.wetland" :label="$t('search.columns.wetland')" @filter:modelValue.self="filterTable" :options="wetlandOption"/>
          </div>
          <div class="column is-2-desktop is-2-tablet" v-if="hasOSRColumn">
            <custom-dropdown v-model="state.searchTerms.osr" :label="$t('search.columns.osr')" @filter:modelValue.self="filterTable" :options="osrOption"/>
          </div>
          <div class="column is-2-desktop is-2-tablet" v-if="hasCMSProfileColumn">
            <custom-dropdown v-model="state.searchTerms.cmsProfile" :label="$t('search.columns.cmsProfile')" @filter:modelValue.self="filterTable" :options="cmsProfileOption"/>
          </div>
        </div>
        <div class="columns">
          <div class="column">
          </div>
        </div>
      </div>

      <div class="filterList">
        <div class="results">
          <span v-show="state.filterTerm">{{ $t("search.resultInfoWithTerms", {filteredRecordNo: state.filteredRecordNo < 0 ? '...' : state.filteredRecordNo, totalRecordNo: state.totalRecordNo, filterTerm: state.filterTerm }) }}</span>
          <span v-show="!state.filterTerm">{{ $t("search.resultInfo", {filteredRecordNo: state.filteredRecordNo < 0 ? '...' : state.filteredRecordNo, totalRecordNo: state.totalRecordNo }) }} </span>
        </div>

        <div class="tags">
          <template v-for="(value, key) in state.searchTerms" v-bind:key="'searchTerm' + key">
            <span  class="tag is-light" v-show="value">
              {{ $t("search.columns." + key) }}: {{value}} <button class="delete is-small" @click="state.searchTerms[key]=''; filterTable();"></button>
            </span>
          </template>
        </div>
        <div class="clearAll" v-show="hasFilter" @click="clearAllFilter">Clear All Filters</div>
        <!-- <div v-show="hasOSRData" style="float: right;">
          <label class="checkbox-label">
            <input type="checkbox" v-model="state.searchTerms.osr" @change="toggleOSR">
            <span class="checkbox-custom rectangular"></span>
            Show species with OSR
          </label>
        </div> -->
      </div>
    </div>
  </div>

</section>

<spp-table/>
</template>

<script>
/* this component hanldes the species table
*/
import { ref, computed } from 'vue';
import { sppSearchState } from '@/composables/state/speciesSearchApp';
import SppTable from './SppTable.vue';
import CustomDropdown from '../../components/biobrowser/CustomDropdown.vue';
import { modelOptions, wetlandOption, osrOption, sppGroupOption, cmsProfileOption } from '@/config/spp-list-config.js';

export default {
  name: 'spp-search-component',
  components: {
    'custom-dropdown': CustomDropdown,
    'spp-table': SppTable
  },
  props: ['year'],
  setup (props) {
    const {
      state,
      clearAllFilter,
      manualSearch,
      searchForAutoCompleteDebounced,
      pickAutoCompleteItem,
      filterTable,
      hasOSRData,
      hasWetlandColumn,
      hasOSRColumn,
      hasCMSProfileColumn
    } = sppSearchState();

    const sppGroupName = sppGroupOption.find(x => x.id === state.groupId)?.shortName;
    const profileImageUrlPrefix = import.meta.env.VITE_APP_PHOTO_PROFILE_URL_PREFIX;
    const introductionUrl = (import.meta.env.VITE_APP_SPP_INTRODUCTION_URL || '').replace('sppGroup', sppGroupName);
    const showAdvancedFilter = ref(false);

    const toggleOSR = (event) => {
      filterTable();
    }

    const tableHeaderStyle = computed(() => {
      return {
        'is-3-desktop is-3-tablet': hasWetlandColumn || hasOSRColumn || hasCMSProfileColumn,
        'is-3-desktop is-3-tablet z': hasWetlandColumn || hasOSRColumn || hasCMSProfileColumn
      };
    });
    const hasFilter = computed(() => {
      const check = Object.keys(state.searchTerms).some(x => state.searchTerms[x]) || state.filterTerm;
      return check; // Object.keys(state.searchTerms).some(x => state.searchTerms[x]) || state.filterTerm;
    });
    return {
      state,
      introductionUrl,
      tableHeaderStyle,
      modelOptions,
      wetlandOption,
      osrOption,
      cmsProfileOption,
      clearAllFilter,
      manualSearch,
      searchForAutoCompleteDebounced,
      pickAutoCompleteItem,
      filterTable,

      showAdvancedFilter,
      hasFilter,
      toggleOSR,
      hasOSRData,
      hasWetlandColumn,
      hasOSRColumn,
      hasCMSProfileColumn,
      profileImageUrlPrefix,
    }
  }
}
</script>

<style scoped>

div#filterExpand {
  margin-top: -30px;
}
.filterList {
  margin-top: -10px;
}
.section.speciesSearch {
  padding: 0 1.5rem;
}
.backButton {
  display: flex;
  /* align-content: stretch; */
  /* justify-items: flex-start; */
  align-items: center;
  /* justify-content: stretch; */
}
.backButton .label {
  padding: 0 20px 0 0px;
  text-transform: uppercase;
}
.filterToggleButton  {
  padding-bottom: 0;
}
.checkbox-label .checkbox-custom::before {
  position: absolute;
  content: "";
  left: 10px;
  top: 10px;
  width: 0px;
  height: 0px;
  border-radius: 5px;
  border: 2px solid #333;
  -webkit-transform: scale(0);
  -ms-transform: scale(0);
  transform: scale(0);
}

.checkbox-label input:checked ~ .checkbox-custom::before {
  left: -3px;
  top: -3px;
  border-radius: 5px;
  -webkit-transform: scale(3);
  -ms-transform: scale(3);
  transform: scale(3);
  opacity:0;
  z-index: 999;
  transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -o-transition: all 0.3s ease-out;
}

.checkbox-label .checkbox-custom.radio, .checkbox-label input:checked ~ .checkbox-custom.radio {
  border-radius: 25px;
}
.checkbox-label input:checked ~ .checkbox-custom.radio::after {
  -webkit-transform: scale(1);
  -ms-transform:  scale(1);
  opacity: 1;
  left: 5px;
  top: 5px;
  width: 6px;
  height: 6px;
  border-radius: 25px!important;
  background-color: black;
}
</style>
